import React, { useEffect, useState } from 'react';
import EmailProviderSettings from './EmailProviderSettings';

// Clipboard helper that works on HTTP origins (navigator.clipboard is only
// available on HTTPS/localhost). Reused from ChannelManagerSettings.js.
const copyText = async (text) => {
  const s = String(text ?? '');
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(s);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = s;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
};

const PROVIDER_DEFAULTS = {
  NIMBUS: {
    base_url: 'https://nimbusit.net/api/pushsms',
    extra_params_json: { rpt: 1 },
  },
};

const emptyForm = () => ({
  id: null,
  provider_name: 'NIMBUS',
  display_name: '',
  base_url: PROVIDER_DEFAULTS.NIMBUS.base_url,
  auth_user: '',
  auth_key: '',
  sender_id: '',
  entity_id: '',
  default_template_id: '',
  route: '',
  extra_params_json: { rpt: 1 },
  is_active: true,
  is_default: false,
  auth_key_set: false,
  auth_key_masked: '',
});

export default function SmsProviderSettings({ token, selectedProperty, role }) {
  // SMS + Email configuration is managed by hotel-admin (and super-admin) users.
  const canConfig = role === 'admin' || role === 'hotel_admin';
  const [activeTab, setActiveTab] = useState('sms');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Auth key edit-unlock pattern (same as SSO Service Key)
  const [authKeyEditing, setAuthKeyEditing] = useState(false);

  // Test panel state — one "open" per provider id
  const [testOpenId, setTestOpenId] = useState(null);
  const [testMobile, setTestMobile] = useState('');
  const [testText, setTestText] = useState('');
  const [testTemplateId, setTestTemplateId] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Recent send log
  const [logs, setLogs] = useState([]);
  const [logsOpen, setLogsOpen] = useState(false);

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  });

  const clearBanners = () => { setError(''); setMessage(''); };

  const load = async () => {
    clearBanners();
    setLoading(true);
    try {
      const res = await fetch('/api/admin/sms-providers', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load providers');
      setProviders(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  const loadLogs = async () => {
    try {
      const res = await fetch('/api/admin/sms-send-log?limit=20', { headers: headers() });
      const data = await res.json();
      if (res.ok) setLogs(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openCreate = () => {
    setForm(emptyForm());
    setAuthKeyEditing(true);
    setEditOpen(true);
    clearBanners();
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      provider_name: row.provider_name || 'NIMBUS',
      display_name: row.display_name || '',
      base_url: row.base_url || '',
      auth_user: row.auth_user || '',
      auth_key: '',                              // blank = keep existing
      sender_id: row.sender_id || '',
      entity_id: row.entity_id || '',
      default_template_id: row.default_template_id || '',
      route: row.route || '',
      extra_params_json: row.extra_params_json || { rpt: 1 },
      is_active: row.is_active !== false,
      is_default: row.is_default === true,
      auth_key_set: row.auth_key_set === true,
      auth_key_masked: row.auth_key_masked || '',
    });
    setAuthKeyEditing(false);
    setEditOpen(true);
    clearBanners();
  };

  const save = async () => {
    clearBanners();
    setSaving(true);
    try {
      const payload = {
        provider_name: form.provider_name,
        display_name: form.display_name || null,
        base_url: form.base_url,
        auth_user: form.auth_user || null,
        sender_id: form.sender_id || null,
        entity_id: form.entity_id || null,
        default_template_id: form.default_template_id || null,
        route: form.route || null,
        extra_params_json: form.extra_params_json || null,
        is_active: !!form.is_active,
        is_default: !!form.is_default,
      };
      // auth_key rules:
      //   - Create (no id) OR edit with unlocked field AND non-empty string → send new value
      //   - Edit with unlocked field AND empty → explicitly send '' to clear
      //   - Edit with locked field → omit (server keeps existing)
      if (!form.id || authKeyEditing) {
        payload.auth_key = form.auth_key || '';
      }
      const url = form.id ? `/api/admin/sms-providers/${form.id}` : '/api/admin/sms-providers';
      const method = form.id ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      setMessage(form.id ? `Updated "${data.display_name || data.provider_name}"` : `Created "${data.display_name || data.provider_name}"`);
      setEditOpen(false);
      await load();
    } catch (err) {
      setError(err.message);
    } finally { setSaving(false); }
  };

  const setAsDefault = async (id) => {
    clearBanners();
    try {
      const res = await fetch(`/api/admin/sms-providers/${id}/set-default`, { method: 'POST', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed');
      setMessage('Default provider updated');
      await load();
    } catch (err) { setError(err.message); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete SMS provider "${row.display_name || row.provider_name}"? This cannot be undone.`)) return;
    clearBanners();
    try {
      const res = await fetch(`/api/admin/sms-providers/${row.id}`, { method: 'DELETE', headers: headers() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Delete failed');
      }
      setMessage('Provider deleted');
      await load();
    } catch (err) { setError(err.message); }
  };

  const openTest = (row) => {
    setTestOpenId(testOpenId === row.id ? null : row.id);
    setTestMobile('');
    setTestText('Your Login OTP is 123456 Please do not share it with anyone. Easeventure');
    setTestTemplateId('');
    setTestResult(null);
  };

  const sendTest = async (row) => {
    if (!/^[6-9]\d{9}$/.test(testMobile)) { setError('Enter a valid 10-digit Indian mobile (starts with 6-9)'); return; }
    if (!testText.trim()) { setError('Enter the text body to send'); return; }
    clearBanners();
    setTestRunning(true);
    setTestResult(null);
    try {
      const body = { mobile: testMobile.trim(), text: testText };
      if (testTemplateId.trim()) body.template_id = testTemplateId.trim();
      const res = await fetch(`/api/admin/sms-providers/${row.id}/test`, {
        method: 'POST', headers: headers(), body: JSON.stringify(body),
      });
      const data = await res.json();
      setTestResult(data);
      if (logsOpen) loadLogs();
    } catch (err) {
      setTestResult({ success: false, error_message: err.message });
    } finally { setTestRunning(false); }
  };

  const toggleLogs = () => {
    const next = !logsOpen;
    setLogsOpen(next);
    if (next) loadLogs();
  };

  // ── Styling ─────────────────────────────────────────────────────────────
  const card = { background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '1rem 1.25rem', marginBottom: '1rem' };
  const label = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 4 };
  const inputStyle = { width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' };
  const btn = { padding: '0.45rem 0.9rem', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 };
  const pill = (bg, fg) => ({ background: bg, color: fg, padding: '2px 8px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.02em' });

  return (
    <div style={{ padding: '1.25rem 1.5rem', background: '#f9fafb', minHeight: '80vh' }}>
      {/* Tabs: SMS Providers (global, admin) + Email / SMTP (per-property) */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '1px solid #e2e8f0' }}>
        {canConfig && (
          <button onClick={() => setActiveTab('sms')} style={{ padding: '0.5rem 1rem', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem', color: activeTab === 'sms' ? '#1e40af' : '#64748b', borderBottom: activeTab === 'sms' ? '2px solid #1e40af' : '2px solid transparent' }}>SMS Providers</button>
        )}
        <button onClick={() => setActiveTab('email')} style={{ padding: '0.5rem 1rem', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem', color: activeTab === 'email' ? '#1e40af' : '#64748b', borderBottom: activeTab === 'email' ? '2px solid #1e40af' : '2px solid transparent' }}>Email / SMTP</button>
      </div>

      {activeTab === 'email' && <EmailProviderSettings token={token} selectedProperty={selectedProperty} role={role} />}

      {activeTab === 'sms' && canConfig && (<>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#1e3a8a' }}>SMS Providers</h2>
          <div style={{ color: '#6b7280', fontSize: '0.82rem', marginTop: 2 }}>
            Global SMS configuration. Configure one or more providers; exactly one can be the default. All downstream SMS (OTP, booking confirm, etc.) use the default unless overridden.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={toggleLogs} style={{ ...btn, background: '#fff', color: '#1e40af', border: '1px solid #dbeafe' }}>{logsOpen ? 'Hide' : 'View'} Recent Sends</button>
          <button onClick={openCreate} style={{ ...btn, background: '#1e40af', color: '#fff' }}>+ Add Provider</button>
        </div>
      </div>

      {error && <div style={{ ...card, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>{error}</div>}
      {message && <div style={{ ...card, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' }}>{message}</div>}

      {/* Providers list */}
      {loading && <div style={card}>Loading…</div>}
      {!loading && providers.length === 0 && (
        <div style={{ ...card, textAlign: 'center', color: '#6b7280' }}>
          No providers configured yet. Click <b>+ Add Provider</b> to set up Nimbus.
        </div>
      )}
      {providers.map(p => {
        const testIsOpen = testOpenId === p.id;
        return (
          <div key={p.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1e3a8a' }}>{p.display_name || p.provider_name}</span>
                  <span style={pill('#e0e7ff', '#3730a3')}>{p.provider_name}</span>
                  {p.is_default && <span style={pill('#dcfce7', '#166534')}>● DEFAULT</span>}
                  {p.is_active
                    ? <span style={pill('#dbeafe', '#1e40af')}>Active</span>
                    : <span style={pill('#fef3c7', '#92400e')}>Inactive</span>
                  }
                </div>
                <div style={{ color: '#6b7280', fontSize: '0.8rem', fontFamily: 'monospace' }}>{p.base_url}</div>
                <div style={{ color: '#6b7280', fontSize: '0.78rem', marginTop: 2 }}>
                  Sender: <b style={{ color: '#1f2937' }}>{p.sender_id || '—'}</b>
                  {' · '}User: <b style={{ color: '#1f2937' }}>{p.auth_user || '—'}</b>
                  {' · '}Key: <span style={{ fontFamily: 'monospace', color: p.auth_key_set ? '#065f46' : '#b91c1c' }}>{p.auth_key_masked || '— not set —'}</span>
                </div>
                <div style={{ color: '#6b7280', fontSize: '0.78rem', marginTop: 2 }}>
                  DLT Entity: <b style={{ color: '#1f2937' }}>{p.entity_id || '—'}</b>
                  {' · '}Default Template: <b style={{ color: '#1f2937', fontFamily: 'monospace' }}>{p.default_template_id || '—'}</b>
                  {p.route ? <>{' · '}Route: <b style={{ color: '#1f2937' }}>{p.route}</b></> : null}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {!p.is_default && p.is_active && (
                  <button onClick={() => setAsDefault(p.id)} style={{ ...btn, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' }}>Set Default</button>
                )}
                <button onClick={() => openTest(p)} style={{ ...btn, background: testIsOpen ? '#fbbf24' : '#fef3c7', color: '#78350f', border: '1px solid #fde68a' }}>{testIsOpen ? 'Close Test' : 'Test'}</button>
                <button onClick={() => openEdit(p)} style={{ ...btn, background: '#fff', color: '#1e40af', border: '1px solid #dbeafe' }}>Edit</button>
                <button onClick={() => remove(p)} style={{ ...btn, background: '#fff', color: '#991b1b', border: '1px solid #fecaca' }}>Delete</button>
              </div>
            </div>

            {/* Test panel */}
            {testIsOpen && (
              <div style={{ marginTop: '0.9rem', padding: '0.8rem 1rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
                <div style={{ fontWeight: 700, color: '#78350f', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Send Test SMS via {p.display_name || p.provider_name}</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 160px' }}>
                    <label style={label}>Mobile (10-digit)</label>
                    <input value={testMobile} onChange={e => setTestMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="9840000000" style={inputStyle} />
                  </div>
                  <div style={{ flex: '1 1 160px' }}>
                    <label style={label}>Template ID override (optional)</label>
                    <input value={testTemplateId} onChange={e => setTestTemplateId(e.target.value)} placeholder="leave blank to use default" style={{ ...inputStyle, fontFamily: 'monospace' }} />
                  </div>
                </div>
                <div style={{ marginTop: '0.5rem' }}>
                  <label style={label}>Message body (must match DLT-registered template)</label>
                  <textarea value={testText} onChange={e => setTestText(e.target.value)} rows={2}
                    style={{ ...inputStyle, resize: 'vertical', fontSize: '0.85rem' }} />
                  <div style={{ fontSize: '0.72rem', color: '#92400e', marginTop: 2 }}>{testText.length} chars</div>
                </div>
                <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => sendTest(p)} disabled={testRunning}
                    style={{ ...btn, background: testRunning ? '#94a3b8' : '#f59e0b', color: '#fff' }}>
                    {testRunning ? 'Sending…' : 'Send Test'}
                  </button>
                  {testResult && (
                    <span style={{ fontSize: '0.82rem', color: testResult.success ? '#065f46' : '#991b1b', fontWeight: 600 }}>
                      {testResult.success ? '✓ SUBMITTED' : '✗ FAILED'}
                      {testResult.http_status ? ` · HTTP ${testResult.http_status}` : ''}
                      {testResult.message_id ? ` · id ${String(testResult.message_id).slice(-24)}` : ''}
                    </span>
                  )}
                </div>
                {testResult && (
                  <details style={{ marginTop: '0.5rem', fontSize: '0.78rem' }}>
                    <summary style={{ cursor: 'pointer', color: '#78350f' }}>Provider response + log id</summary>
                    <div style={{ padding: '0.5rem 0.6rem', background: '#fff', borderRadius: 6, marginTop: 4, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      log_id: {testResult.log_id || '—'}<br/>
                      error: {testResult.error_message || '—'}<br/>
                      raw: {testResult.provider_response || '—'}
                    </div>
                  </details>
                )}
                <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: '#78350f' }}>
                  Tip: if the provider says SUBMITTED but the phone doesn't receive the SMS, the operator dropped it — usually a DLT registration mismatch. Check Sender ID + Template ID with the provider support.
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Recent send log */}
      {logsOpen && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
            <div style={{ fontWeight: 700, color: '#1e3a8a' }}>Recent SMS sends (last 20)</div>
            <button onClick={loadLogs} style={{ ...btn, background: '#fff', color: '#1e40af', border: '1px solid #dbeafe' }}>Refresh</button>
          </div>
          {logs.length === 0 && <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>No send log rows yet.</div>}
          {logs.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9', color: '#334155' }}>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>#</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>When</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>Mobile</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>Sender</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>Template</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>Triggered</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>Body</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(l => {
                    const senderMatch = String(l.request_url || '').match(/[?&]sender=([^&]+)/);
                    const sender = senderMatch ? decodeURIComponent(senderMatch[1]) : '—';
                    return (
                      <tr key={l.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '0.35rem 0.5rem', color: '#94a3b8' }}>{l.id}</td>
                        <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap' }}>{new Date(l.created_at).toLocaleString()}</td>
                        <td style={{ padding: '0.35rem 0.5rem', fontFamily: 'monospace' }}>{l.mobile}</td>
                        <td style={{ padding: '0.35rem 0.5rem' }}>{sender}</td>
                        <td style={{ padding: '0.35rem 0.5rem', fontFamily: 'monospace', fontSize: '0.72rem' }}>{l.template_id || '—'}</td>
                        <td style={{ padding: '0.35rem 0.5rem' }}>
                          {l.success
                            ? <span style={pill('#dcfce7', '#166534')}>OK {l.http_status}</span>
                            : <span style={pill('#fee2e2', '#991b1b')}>FAIL {l.http_status || ''}</span>}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', color: '#64748b', fontSize: '0.72rem' }}>{l.triggered_by || '—'}</td>
                        <td style={{ padding: '0.35rem 0.5rem', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.text_snippet || ''}>{l.text_snippet || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Edit / Create modal */}
      {editOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ width: 'min(720px,96vw)', maxHeight: '92vh', overflow: 'auto', background: '#fff', borderRadius: 14, boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '0.9rem 1.25rem', background: '#1e3a8a', color: '#fff', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 800, fontSize: '1.02rem' }}>
                {form.id ? `Edit Provider #${form.id}` : 'New SMS Provider'}
              </div>
              <button onClick={() => setEditOpen(false)} style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', width: 30, height: 30, borderRadius: 8, cursor: 'pointer', fontSize: '1rem', fontWeight: 700 }}>✕</button>
            </div>
            <div style={{ padding: '1rem 1.25rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
                <div>
                  <label style={label}>Provider</label>
                  <select value={form.provider_name}
                    onChange={e => {
                      const name = e.target.value;
                      setForm(f => ({
                        ...f,
                        provider_name: name,
                        base_url: f.id ? f.base_url : (PROVIDER_DEFAULTS[name]?.base_url || f.base_url),
                        extra_params_json: f.id ? f.extra_params_json : (PROVIDER_DEFAULTS[name]?.extra_params_json || f.extra_params_json),
                      }));
                    }}
                    style={inputStyle}>
                    <option value="NIMBUS">Nimbus</option>
                    {/* future providers append here */}
                  </select>
                </div>
                <div>
                  <label style={label}>Display Name</label>
                  <input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} placeholder="Nimbus Primary" style={inputStyle} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={label}>Base URL</label>
                  <input value={form.base_url} onChange={e => setForm(f => ({ ...f, base_url: e.target.value }))} style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '0.82rem' }} />
                </div>
                <div>
                  <label style={label}>Auth User</label>
                  <input value={form.auth_user} onChange={e => setForm(f => ({ ...f, auth_user: e.target.value }))} placeholder="210901" style={inputStyle} />
                </div>
                <div>
                  <label style={label}>Auth Key</label>
                  {form.id && !authKeyEditing ? (
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <input readOnly value={form.auth_key_masked || (form.auth_key_set ? '••••••••' : '— not set —')} style={{ ...inputStyle, background: '#f8fafc', fontFamily: 'monospace' }} />
                      <button type="button" onClick={() => { setAuthKeyEditing(true); setForm(f => ({ ...f, auth_key: '' })); }} style={{ ...btn, background: '#fff', color: '#374151', border: '1px solid #d1d5db' }}>Edit / Paste</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <input value={form.auth_key} onChange={e => setForm(f => ({ ...f, auth_key: e.target.value.trim() }))} placeholder="Paste the provider's API key" style={{ ...inputStyle, fontFamily: 'monospace' }} />
                      {form.id && <button type="button" onClick={() => { setAuthKeyEditing(false); setForm(f => ({ ...f, auth_key: '' })); }} style={{ ...btn, background: '#fff', color: '#374151', border: '1px solid #d1d5db' }}>Cancel</button>}
                    </div>
                  )}
                </div>
                <div>
                  <label style={label}>Sender ID (DLT header, 6 chars)</label>
                  <input value={form.sender_id} onChange={e => setForm(f => ({ ...f, sender_id: e.target.value.toUpperCase().slice(0, 12) }))} placeholder="EVPLER" style={{ ...inputStyle, fontFamily: 'monospace' }} />
                </div>
                <div>
                  <label style={label}>Route (optional)</label>
                  <select value={form.route || ''} onChange={e => setForm(f => ({ ...f, route: e.target.value || null }))} style={inputStyle}>
                    <option value="">—</option>
                    <option value="TRANS">TRANS</option>
                    <option value="PROMO">PROMO</option>
                    <option value="OTP">OTP</option>
                    <option value="SERVICE">SERVICE</option>
                  </select>
                </div>
                <div>
                  <label style={label}>DLT Entity ID</label>
                  <input value={form.entity_id} onChange={e => setForm(f => ({ ...f, entity_id: e.target.value }))} placeholder="1001761041718726690" style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '0.8rem' }} />
                </div>
                <div>
                  <label style={label}>Default Template ID</label>
                  <input value={form.default_template_id} onChange={e => setForm(f => ({ ...f, default_template_id: e.target.value }))} placeholder="1707177651421899895" style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '0.8rem' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={label}>Extra Params JSON (provider-specific)</label>
                  <textarea value={JSON.stringify(form.extra_params_json || {}, null, 2)}
                    onChange={e => {
                      try { const parsed = JSON.parse(e.target.value || '{}'); setForm(f => ({ ...f, extra_params_json: parsed })); setError(''); }
                      catch { setError('extra_params_json is not valid JSON'); }
                    }}
                    rows={3}
                    style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '0.8rem', resize: 'vertical' }} />
                </div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} /> Active
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!form.is_default} onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))} /> Default provider
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button onClick={() => setEditOpen(false)} disabled={saving} style={{ ...btn, background: '#fff', color: '#374151', border: '1px solid #d1d5db' }}>Cancel</button>
                <button onClick={save} disabled={saving} style={{ ...btn, background: saving ? '#94a3b8' : '#1e40af', color: '#fff' }}>
                  {saving ? 'Saving…' : (form.id ? 'Save changes' : 'Create provider')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
